
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
  email: z.string().email(),
  name: z.string().min(2),
});

interface WaitlistFormProps {
  buttonLabel?: string;
}

const WaitlistForm = ({ buttonLabel }: WaitlistFormProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();

  const content = {
    fr: {
      title: "Rejoignez la liste d'attente",
      description: "Soyez les premiers informés du lancement",
      nameLabel: "Nom",
      emailLabel: "Email",
      button: "Rejoindre la liste d'attente",
      success: "Merci ! Nous vous contacterons dès le lancement.",
    },
    en: {
      title: "Join the waitlist",
      description: "Be the first to know when we launch",
      nameLabel: "Name",
      emailLabel: "Email",
      button: "Join waitlist",
      success: "Thanks! We'll contact you when we launch.",
    },
    de: {
      title: "Auf die Warteliste",
      description: "Seien Sie die Ersten, die vom Start erfahren",
      nameLabel: "Name",
      emailLabel: "E-Mail",
      button: "Warteliste beitreten",
      success: "Danke! Wir kontaktieren Sie beim Start.",
    },
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      name: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert([
          {
            email: values.email,
            name: values.name,
            language: language
          }
        ]);

      if (error) {
        if (error.code === '23505') {
          // Unique violation error code
          toast({
            title: "Already registered",
            description: "This email is already on our waitlist.",
            variant: "destructive",
          });
        } else {
          throw error;
        }
        return;
      }

      toast({
        title: content[language].success,
        duration: 3000,
      });
      
      form.reset();
    } catch (error) {
      console.error('Error submitting to waitlist:', error);
      toast({
        title: "Error",
        description: "There was an error joining the waitlist. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="p-6 bg-white/80 backdrop-blur-sm rounded-lg shadow max-w-md mx-auto">
      <h3 className="text-2xl font-bold mb-2">{content[language].title}</h3>
      <p className="text-gray-600 mb-6">{content[language].description}</p>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{content[language].nameLabel}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{content[language].emailLabel}</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button 
            type="submit" 
            className="w-full bg-legatia-orange hover:bg-legatia-orange/90 text-white text-lg py-3"
          >
            {buttonLabel || content[language].button}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default WaitlistForm;
